import { MaterialTree } from '../models/material-tree';
import { ItemFlatNode, ItemNode } from '../models/item';
import { TreeUtils } from './tree-utils';
import { ItemNodeUtils } from './item-node-utils';

export class NodeSelectionHandler {
    highlightedNode: ItemNode;
    private itemNodeUtils: ItemNodeUtils;
    constructor(private materialTree: MaterialTree, private treeUtils: TreeUtils, private maxSelectableItemsNotify, private notSelectableItemsNotify, private autoSelectParent) {
        this.itemNodeUtils = new ItemNodeUtils(materialTree.dataSource.data);
        if (materialTree.dataSource.data.length) {
            this.highlightedNode = this.itemNodeUtils.findNodeById(materialTree.dataSource.data[0].Id);
        }
    }

    // l'utente ha cliccato su una checkbox
    onItemCheckboxChange(node: ItemFlatNode): void {
        if (!node.expandable || node.checkable === true) { // è checkabile (è una foglia oppure è un padre checkable)
            const currentCheckboxCount = (!this.materialTree.checklistSelection.isSelected(node)) ? +1 : -1;
            const parentNode = this.treeUtils.getParentNode(node);

            if (parentNode) { // ha un padre
                const descendants = this.materialTree.treeControl.getDescendants(parentNode);
                const checkedCount = descendants.filter(
                    child => this.materialTree.checklistSelection.isSelected(child) && node.level === child.level
                ).length + currentCheckboxCount;
                if (parentNode.maxSelectableChildren) { // il padre ha un vincolo sul numero dei figli
                    const tooMany = (checkedCount >= parentNode.maxSelectableChildren);
                    if (tooMany) {
                        descendants.forEach(child => {
                            if (node.level === child.level) {
                                child.selectable = (this.materialTree.checklistSelection.isSelected(child) || node === child);
                            }
                        });
                    } else {
                        descendants.forEach(child => {
                            if (node.level === child.level) {
                                child.selectable = true;
                            }
                        });
                    }
                }
                if (this.autoSelectParent && parentNode.checkable && checkedCount === 1) { // se il padre è checkable ed è il primo figlio selezionato
                    const isSelected = this.materialTree.checklistSelection.isSelected(node);
                    if (!isSelected) {
                        this.materialTree.checklistSelection.select(parentNode);
                    }
                }
            }

            this.materialTree.checklistSelection.toggle(node);

            if (node.expandable && node.checkable === true) { // è un padre ed è checkabile // anche selectable?
                const isSelected = this.materialTree.checklistSelection.isSelected(node);
                if (isSelected) { // l'ho checkato
                    this.treeUtils.expandNode(node);
                }
                else { // l'ho uncheckato
                    if (this.autoSelectParent && node.maxSelectableChildren === 1) { // è un radio
                        const descendants = this.materialTree.treeControl.getDescendants(node);
                        descendants.forEach(child => { this.materialTree.checklistSelection.deselect(child); });
                    }
                }
            }
            this.treeUtils.notifySelectedItems();
        }
    }

    // l'utente ha cliccato su un radio button
    onItemRadioClick(node: ItemFlatNode): void {
        this.highlightedNode = this.itemNodeUtils.findNodeById(node.Id);
        const parentNode = this.treeUtils.getParentNode(node);
        if (parentNode) { // ha un padre
            const descendants = this.materialTree.treeControl.getDescendants(parentNode);
            descendants.forEach(child => { this.materialTree.checklistSelection.deselect(child); });
            if (this.autoSelectParent && parentNode.checkable) { // anche selectable?
                this.materialTree.checklistSelection.select(parentNode);
            }
        }
        if (node.selectable !== false) {
            this.materialTree.checklistSelection.select(node);
        }
        this.treeUtils.notifySelectedItems();
    }

    // l'utente ha cliccato su un elemento in modo generico
    onItemClick(clickedNode) {
        this.highlightedNode = this.itemNodeUtils.findNodeById(clickedNode.Id);
    }

    onNodeSelectClick(node: ItemFlatNode) {
        const parentNode = this.treeUtils.getParentNode(node);
        if (parentNode) { // se non ha un padre è a livello 0, ed anche se checckabile vince sempre sul figlio, quindi non serve evento
            if (!node.expandable || node.checkable === true) { // se è un figlio oppure è checkabile
                if (parentNode.maxSelectableChildren && (parentNode.checkable === false || !this.materialTree.checklistSelection.isSelected(parentNode))) {
                    this.emitMaxSelectableEvent(node);
                } else {
                    this.emitNotSelectableEvent(node);
                }
            }
        }
    }

    emitMaxSelectableEvent(node: ItemFlatNode) {
        if (node.selectable === false) {
            this.maxSelectableItemsNotify.emit();
        }
    }

    emitNotSelectableEvent(node: ItemFlatNode) {
        if (node.selectable === false) {
            this.notSelectableItemsNotify.emit();
        }
    }

    toggleHighlightedNode() {
        if (this.highlightedNode) {
            const node = this.treeUtils.findNodeById(this.highlightedNode.Id);
            this.onNodeSelectClick(node);
            if (this.treeUtils.hasMaxSelectableChildren(node) === 1) {
                this.onItemRadioClick(node);
            } else {
                // se è un figlio ed è selectabile oppure è un padre che si può selezionare lui stesso
                if (node.selectable !== false && (!node.expandable || (node.expandable && node.checkable === true))) {
                    this.onItemCheckboxChange(node);
                }
            }
        }
    }

    highlightNext() {
        const nextSibling = this.itemNodeUtils.getNextSibling(this.highlightedNode);
        if (nextSibling) {
            this.highlightedNode = nextSibling;
            const element = document.querySelector('.selected-item')?.nextElementSibling;
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' }); // false
            }
        } else { // ultimo record in basso
            const parent = this.itemNodeUtils.getParent(this.highlightedNode);
            const nextUncle = this.itemNodeUtils.getNextSibling(parent);
            if (nextUncle) {
                this.highlightedNode = nextUncle;
            }
            const element = document.querySelector('.item-tree');
            if (element) {
                element.scrollTop = 0;
            }
        }
    }

    highlightPrevious() {
        const previousSibling = this.itemNodeUtils.getPreviousSibling(this.highlightedNode);
        if (previousSibling) {
            this.highlightedNode = previousSibling;
            const element = document.querySelector('.selected-item')?.previousElementSibling;
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            }
        } else {
            const parent = this.itemNodeUtils.getParent(this.highlightedNode);
            const previousUncle = this.itemNodeUtils.getPreviousSibling(parent);
            if (previousUncle) {
                this.highlightedNode = previousUncle;
            }
            const element = document.querySelector('.item-tree');
            if (element) {
                element.scrollTop = element.scrollHeight;
            }
        }
    }

    highlightChild() {
        if (this.highlightedNode && this.highlightedNode.children && this.highlightedNode.children.length > 0) {
            this.treeUtils.expandNode(this.treeUtils.findNodeById(this.highlightedNode.Id));
            this.highlightedNode = this.highlightedNode.children[0];
        }
    }

    highlightParent() {
        const parentItem = this.itemNodeUtils.getParent(this.highlightedNode);
        if (parentItem) {
            const parentNode = this.treeUtils.findNodeById(parentItem.Id);
            this.treeUtils.collapseNode(parentNode);
            this.highlightedNode = parentItem;
        } else {
            if (this.highlightedNode) {
                const currentNode = this.treeUtils.findNodeById(this.highlightedNode.Id);
                this.treeUtils.collapseNode(currentNode);
            }
        }
    }

}
